import { Alert, Box, Grid, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FE_ROLES, FORM } from '../../config/consts';
import { getCurrentRole } from '../../helpers/user';
import { useSurvicateEvent } from '../../hooks/useSurvicateEvent';
import apartmentActions from '../../modules/apartments';
import { CONTEXT } from '../../modules/user';
import { isAdmin } from '../../modules/user/utils';
import CreateOrEditModalDialog from '../_DesignSystemWrappers/Modal/CreateOrEditModalDialog';
import ApartmentAutocomplete from '../ApartmentAutocomplete';
import Checkbox from '../HookFormFields/Checkbox';
import TextField from '../HookFormFields/TextField';
import { UserCategoryFields } from '../UserCategoryFields/UserCategoryFields';
import UserRoleSelect from '../UserRoleSelect';
import { getInitialFormData, getSubmitButtonText, getSubmitFormCallback, getTitle } from './helpers/utils';
import { useGetApartmentFormData } from './hooks/getApartmentFormData';
import { useGetGlobalHooks } from './hooks/getGlobalHooks';
import messages from './messages';

const DEFAULT_DATA = {
  companies: [],
  email: '',
  firstName: '',
  id: null,
  lastName: '',
};

function UserModal({
  closeButtonDataTestId,
  context,
  data = DEFAULT_DATA,
  isCurrentUser = false,
  isEdit = false,
  onClose,
  open = false,
  primaryButtonDataTestId,
  showApartmentField = false,
}) {
  const newItemId = FORM.NEW_ITEM_ID;
  const hasFormItemKey = FORM.HAS_FORM;
  const [userRole, setSiteUserRole] = useState('');
  const prevUserRoleRef = useRef('');
  const formData = useGetApartmentFormData();
  const { triggerSurveyEvent } = useSurvicateEvent('user_created_site_admin');

  const validation = {
    companyId: formData.companyId,
    siteId: formData.siteId,
  };
  const { dispatch, formatMessage, formMethods } = useGetGlobalHooks(
    isEdit,
    context,
    isCurrentUser,
    data,
    validation,
    formData.companyId,
    formData.siteId,
    userRole
  );
  const phoneNumberValue = formMethods.watch('phoneNumber');
  const isLoginLessOrCompanyAdmin = userRole === FE_ROLES.LOGIN_LESS || userRole === FE_ROLES.COMPANY_ADMIN;
  const isUserAdmin = isAdmin(userRole);
  const isUserRoleField = !isEdit && isUserAdmin;

  useEffect(() => {
    if (!open) {
      prevUserRoleRef.current = '';
      return;
    }

    const getNextRole = () => {
      if (isEdit) {
        return '';
      }

      const roleByContext = {
        [CONTEXT.SITE_USERS]: FE_ROLES.SITE_USER,
        [CONTEXT.COMPANY_ADMINS]: FE_ROLES.COMPANY_ADMIN,
      };

      return roleByContext[context] ?? '';
    };

    queueMicrotask(() => setSiteUserRole(getNextRole()));

    if (showApartmentField) {
      dispatch(apartmentActions.getApartmentsList(formData.companyId, formData.siteId));
    }

    formMethods.reset(getInitialFormData(formData.initialData, hasFormItemKey, data));
  }, [open]);

  useEffect(() => {
    if (!phoneNumberValue) {
      formMethods.setValue('gsmCalls', false);
    }
  }, [phoneNumberValue]);

  useEffect(() => {
    if (userRole === FE_ROLES.LOGIN_LESS) {
      formMethods.resetField('phoneNumber');
    }

    if (!isUserAdmin) {
      formMethods.resetField('userCategory');
      formMethods.resetField('customUserCategory');
    }

    const prevRole = prevUserRoleRef.current;
    prevUserRoleRef.current = userRole;

    if (open && prevRole && userRole && prevRole !== userRole) {
      formMethods.trigger();
    }
  }, [userRole]);

  const submitForm = getSubmitFormCallback({
    context,
    data,
    dispatch,
    formMethods,
    hasFormItemKey,
    initialData: formData.initialData,
    isCurrentUser,
    isEdit,
    newItemId,
    onClose,
    triggerSurveyEvent,
    userRole,
    validation,
  });

  return (
    <FormProvider {...formMethods}>
      <CreateOrEditModalDialog
        closeButtonDataTestId={closeButtonDataTestId}
        isEdit={isEdit}
        open={open}
        primaryButtonAction={formMethods.handleSubmit(submitForm)}
        primaryButtonDataTestId={primaryButtonDataTestId}
        primaryButtonText={formatMessage(getSubmitButtonText(isEdit, context))}
        secondaryButtonAction={onClose}
        secondaryButtonText={formatMessage(messages.userModalFormActionsCancel)}
        onClose={onClose}
        submitDisabled={
          !formMethods.formState.isValid ||
          (!isCurrentUser && !userRole && context !== CONTEXT.COMPANY_ADMINS) ||
          !Object.keys(formMethods.formState.dirtyFields).length ||
          formMethods.formState.isSubmitted
        }
        title={formatMessage(getTitle(isEdit, isCurrentUser, context))}
      >
        <form onSubmit={formMethods.handleSubmit(submitForm)}>
          {!isCurrentUser && (
            <UserRoleSelect
              companyName={formData.companyName}
              onChange={setSiteUserRole}
              userRole={userRole || getCurrentRole(data, validation, context)}
            />
          )}
          {userRole !== FE_ROLES.LOGIN_LESS && (
            <TextField
              control={formMethods.control}
              data-testid="site-users-email-input"
              disabled={isEdit}
              fullWidth
              label={formatMessage(messages.userModalLabelEmail)}
              name="email"
              required={!isEdit}
              sx={{ pb: 2 }}
              variant="outlined"
            />
          )}
          <Grid container spacing={2} sx={{ pb: isUserRoleField ? 2 : 0 }}>
            <Grid size={{ sm: 6, xs: 12 }}>
              <TextField
                control={formMethods.control}
                data-testid="site-users-firstname-input"
                fullWidth
                label={formatMessage(messages.userModalLabelFirstName)}
                name="firstName"
                required={userRole !== FE_ROLES.LOGIN_LESS}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ sm: 6, xs: 12 }}>
              <TextField
                control={formMethods.control}
                data-testid="site-users-lastname-input"
                fullWidth
                label={formatMessage(messages.userModalLabelLastName)}
                name="lastName"
                required
                variant="outlined"
              />
            </Grid>
          </Grid>
          {isUserRoleField && (
            <Grid sx={{ pb: 1 }}>
              <UserCategoryFields
                customUserCategoryLabel={formatMessage(
                  context === CONTEXT.COMPANY_ADMINS
                    ? messages.userModalAddAdminUserCategoryOtherLabel
                    : messages.userModalAddSiteUserUserCategoryOtherLabel
                )}
                userCategoryHelperText={formatMessage(
                  context === CONTEXT.COMPANY_ADMINS
                    ? messages.userModalAddAdminUserCategoryHelperText
                    : messages.userModalAddSiteUserUserCategoryHelperText
                )}
                userCategoryLabel={formatMessage(
                  context === CONTEXT.COMPANY_ADMINS
                    ? messages.userModalAddAdminUserCategoryLabel
                    : messages.userModalAddSiteUserUserCategoryLabel
                )}
                userCategoryPlaceholder={formatMessage(
                  context === CONTEXT.COMPANY_ADMINS
                    ? messages.userModalAddAdminUserCategoryPlaceholder
                    : messages.userModalAddSiteUserUserCategoryPlaceholder
                )}
              />
            </Grid>
          )}
          {!isEdit && !isLoginLessOrCompanyAdmin && (
            <>
              <TextField
                control={formMethods.control}
                data-testid="site-users-phone-input"
                fullWidth
                label={formatMessage(messages.usersFormMobilePhoneLabel)}
                helperText={formatMessage(messages.usersFormMobilePhoneHelperText)}
                placeholder={formatMessage(messages.usersFormMobilePhonePlaceholder)}
                name="phoneNumber"
                variant="outlined"
                sx={{ mb: 0, mt: 2 }}
              />
              {formData.siteData?.services?.GSM_CALLS?.active && (
                <Box sx={{ mb: 3 }}>
                  <Tooltip title={<FormattedMessage {...messages.usersFormAllowCallingTooltip} />}>
                    <div>
                      <Checkbox
                        name="gsmCalls"
                        disabled={!phoneNumberValue}
                        label={
                          <Typography color={phoneNumberValue ? 'textPrimary' : 'secondary'}>
                            <FormattedMessage {...messages.usersFormAllowCallingLabel} />
                          </Typography>
                        }
                        sx={{ ml: 2 }}
                      />
                    </div>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
          {showApartmentField && (
            <ApartmentAutocomplete
              hasFormItemKey={hasFormItemKey}
              newItemId={newItemId}
              optionsData={formData.optionsData}
            />
          )}
          {phoneNumberValue && formMethods.watch('gsmCalls') && formMethods.watch('apartment').length === 0 && (
            <Alert severity="warning">
              <FormattedMessage {...messages.usersFormAllowCallingWarning} />
            </Alert>
          )}
        </form>
      </CreateOrEditModalDialog>
    </FormProvider>
  );
}

UserModal.propTypes = {
  closeButtonDataTestId: PropTypes.string,
  context: PropTypes.string.isRequired,
  data: PropTypes.shape({
    companies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })
    ),
    email: PropTypes.string,
    firstName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lastName: PropTypes.string,
  }),
  isCurrentUser: PropTypes.bool,
  isEdit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  primaryButtonDataTestId: PropTypes.string,
  showApartmentField: PropTypes.bool,
};

export default UserModal;
